import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronDown, Check, Building2, CalendarClock, BookCopy } from 'lucide-react';

// Top-left product switcher. Lets the user move between the Reconciliation
// Hub (role-based home) and the Month-End Close module without typing URLs.
//
// This component is intentionally self-contained: it determines the current
// product from the pathname (anything under /mec is MEC; everything else is
// Hub), so the consumer (TopBar) doesn't need to pass anything in. Closes on
// outside-click and on Escape for basic a11y.
const PRODUCTS = [
  {
    id: 'hub',
    name: 'Reconciliation Hub',
    tagline: 'Daily audit · COD checks · Credits',
    icon: Building2,
    path: '/',
  },
  {
    id: 'mec',
    name: 'Month-End Close',
    tagline: 'Recon · JE · Variance',
    icon: CalendarClock,
    path: '/mec',
  },
  {
    id: 'qbo-reporting',
    name: 'QuickBooks Reporting',
    tagline: 'CoA compare · P&L · Export',
    icon: BookCopy,
    path: '/qbo-reporting',
  },
];

function detectProductId(pathname) {
  if (pathname.startsWith('/mec')) return 'mec';
  if (pathname.startsWith('/qbo-reporting')) return 'qbo-reporting';
  return 'hub';
}

export default function ProductSwitcher() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const currentId = detectProductId(location.pathname);
  const current = PRODUCTS.find((p) => p.id === currentId) || PRODUCTS[0];
  const CurrentIcon = current.icon;

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const handleSelect = (product) => {
    setOpen(false);
    if (product.id === currentId) return;
    navigate(product.path);
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="h-10 pl-2 pr-2.5 rounded-[var(--radius-md)] flex items-center gap-2.5 hover:bg-[var(--color-cream-200)] transition-colors group"
      >
        <span className="w-8 h-8 rounded-[var(--radius-sm)] bg-[var(--color-warm-900)] text-[var(--color-cream-50)] flex items-center justify-center shrink-0">
          <CurrentIcon size={16} />
        </span>
        <span className="hidden sm:flex flex-col items-start min-w-0 leading-tight">
          <span className="text-[10px] font-bold uppercase tracking-[1.2px] text-[var(--color-warm-500)]">
            Product
          </span>
          <span className="text-[13px] font-bold text-[var(--color-warm-900)] truncate max-w-[180px]">
            {current.name}
          </span>
        </span>
        <ChevronDown
          size={14}
          className={`text-[var(--color-warm-500)] transition-transform shrink-0 ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute top-full left-0 mt-1 w-72 bg-[var(--color-cream-50)] border border-[var(--color-warm-200)] rounded-[var(--radius-md)] shadow-[var(--shadow-lg)] overflow-hidden z-40 animate-slide-up"
        >
          <div className="p-2 border-b border-[var(--color-warm-100)]">
            <div className="text-[10px] font-bold uppercase tracking-wide text-[var(--color-warm-500)] px-2 py-1">
              Switch Product
            </div>
          </div>
          <ul className="py-1">
            {PRODUCTS.map((product) => {
              const Icon = product.icon;
              const isActive = product.id === currentId;
              return (
                <li key={product.id}>
                  <button
                    role="option"
                    aria-selected={isActive}
                    onClick={() => handleSelect(product)}
                    className={`w-full text-left px-3 py-2.5 flex items-center gap-3 hover:bg-[var(--color-cream-200)] transition-colors ${
                      isActive ? 'bg-[var(--color-cream-100)]' : ''
                    }`}
                  >
                    <span className={`w-9 h-9 rounded-[var(--radius-sm)] flex items-center justify-center shrink-0 ${
                      isActive
                        ? 'bg-[var(--color-warm-900)] text-[var(--color-cream-50)]'
                        : 'bg-[var(--color-cream-200)] text-[var(--color-warm-700)]'
                    }`}>
                      <Icon size={16} />
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className={`block text-[13px] truncate ${
                        isActive ? 'font-bold text-[var(--color-warm-900)]' : 'font-semibold text-[var(--color-warm-800)]'
                      }`}>
                        {product.name}
                      </span>
                      <span className="block text-[11px] text-[var(--color-warm-500)] truncate">
                        {product.tagline}
                      </span>
                    </span>
                    {isActive && (
                      <Check size={14} className="text-[var(--color-recovery-600)] shrink-0" />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
